import React, { useMemo, useEffect } from 'react';
import { useI18n } from '@oneaudi/i18n-context';
import { LocationFilterHeaderContainer, LocationInputContainer, SearchWrapper, StyledSelect, ClearAllLocationsButton, LocateMeButton, ConsentRequestWrapperMobile, } from '../styles/LocationFilter.styles';
import { useServicesContext } from '../../../context';
import { useDesktopOrMobileView } from '../../../hooks';
import { useFilterData, useClearAllDealerFilters, useApplyFilters } from '../FilterContextHooks';
import CookieConsentRequest from './CookieConsentRequest';
import { FilterContext } from '../FilterContext';
import { setLocationFilterToDefaultState } from '../utils/locationFilterHelpers';
import styled from 'styled-components';
import { Headline, SearchField } from '@oneaudi/unified-web-components';
import { SYS_SPACE_FIXED_450 } from '@oneaudi/unified-web-common';
import { DynamicComponent, dynamicStylingHelper } from '../../utils';
import { SYS_COLOR_SURFACE_NEUTRAL_700_DARK, SYS_GRADIENT_ELEVATION_LEVEL3_FILL_DARK, SYS_SPACE_FIXED_150, SYS_SPACE_FIXED_200, SYS_SPACE_FIXED_300, } from '@oneaudi/unified-web-alpha-common';
import { useMandatoryAreaSearchContext } from '../hooks/MandatoryAreaSearchContext';
import { useGoogleMaps } from '../hooks/loadGoogleLibraries';
export const LocationFilterHeader = ({ inputRef, mapConsentButtonText, overlayRef, openConsentOverlay, setIsTwoClickOverlayOpened, handleLocateMeClick, }) => {
    var _a;
    const { effectiveConsent } = useGoogleMaps();
    const { getConfiguration, getEnvironmentConfig } = useServicesContext();
    const context = React.useContext(FilterContext);
    const { radius, setRadius, searchedCoords } = context;
    const audiContentService = useServicesContext().getAdditionalService('audi-content-service');
    let { mileageUnit } = audiContentService.getContent().fields;
    const { locationFilterConfig_radiusOptions } = audiContentService.getContent()
        .fields;
    const radiusOptions = locationFilterConfig_radiusOptions || [10, 20, 50, 100, 200];
    const clearAllDealerFilters = useClearAllDealerFilters();
    const filterData = useFilterData();
    const isDesktop = useDesktopOrMobileView('1024px') === 'desktop';
    const isMobile = useDesktopOrMobileView('767px') === 'mobile';
    const applyFilter = useApplyFilters();
    const isSomeDealerActive = useMemo(() => {
        var _a, _b;
        const dealersFilterData = (_b = (_a = filterData === null || filterData === void 0 ? void 0 : filterData.groups) === null || _a === void 0 ? void 0 : _a.dealers) === null || _b === void 0 ? void 0 : _b.members;
        return Object.values(dealersFilterData).some((obj) => {
            return obj.active === true;
        });
    }, [filterData === null || filterData === void 0 ? void 0 : filterData.groups.dealers.members]);
    const { enableMandatoryAreaSearch } = useMandatoryAreaSearchContext();
    useEffect(() => {
        if (inputRef) {
            const onScroll = () => {
                if ((inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) && (inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === document.activeElement) {
                    inputRef === null || inputRef === void 0 ? void 0 : inputRef.current.blur();
                }
            };
            if (typeof window !== 'undefined') {
                overlayRef.current.removeEventListener('scroll', onScroll);
                overlayRef.current.addEventListener('scroll', onScroll, { passive: true });
            }
        }
    }, [inputRef]);
    const locationSearchHeadlineLabel = useI18n({
        id: `nemo.sc.filter.locationfilter.locationSearchHeadlineLabel`,
        defaultMessage: 'Location',
    });
    const locationSearchInputLabel = useI18n({
        id: `nemo.sc.plp.locationFilter.locationSearchDefaultInputLabel`,
        defaultMessage: '',
    });
    const clearLocationSearchInputButtonAriaLabel = useI18n({
        id: `nemo.sc.filter.locationfilter.clearLocationSearchInputButtonAriaLabel`,
        defaultMessage: 'Clear the text in the location search input field.',
    });
    const locateMeButtonAriaLabel = useI18n({
        id: `nemo.sc.filter.locationfilter.cookieConsentRequestMap.locateMeButtonAriaLabel`,
        defaultMessage: 'Use your location.',
    });
    const distanceRadiusLabel = useI18n({
        id: `nemo.sc.filter.locationfilter.distanceRadiusLabel`,
        defaultMessage: 'Radius',
    });
    const clearAllLocationsButtonText = useI18n({
        id: `nemo.sc.filter.locationfilter.clearAllLocationsButtonText`,
        defaultMessage: 'Reset location filter',
    });
    const locationIsSearchedOrDealerIsActive = searchedCoords !== undefined || isSomeDealerActive;
    const geoFilterDTO = {
        id: 'geo',
        active: true,
        type: 'location-filter',
        values: [
            searchedCoords === null || searchedCoords === void 0 ? void 0 : searchedCoords.latitude,
            searchedCoords === null || searchedCoords === void 0 ? void 0 : searchedCoords.longitude,
            radius,
            mileageUnit,
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.value,
        ],
    };
    const autocompleteCSS = `
    .pac-container {
      background: ${dynamicStylingHelper('#ffffff', SYS_GRADIENT_ELEVATION_LEVEL3_FILL_DARK)};
      padding-block: ${dynamicStylingHelper('24px 24px 24px 24px', `${SYS_SPACE_FIXED_300} ${SYS_SPACE_FIXED_200} ${SYS_SPACE_FIXED_200} ${SYS_SPACE_FIXED_200}`)};
      position: fixed !important;
      border-radius: ${dynamicStylingHelper('0px', '20px')};
    }
    .pac-icon {
      display: none;
    }
   .pac-item,.pac-item-query, .pac-matched {
      color: ${dynamicStylingHelper('#333333', '#ccc')};
      font-size: 16px;
      font-family: AudiType,sans-serif;
      font-stretch: 105%;
      line-height: 24px;
      font-weight: 400;
      border-top: none;
    }
    .pac-item{
      padding: 0px;
      padding: ${dynamicStylingHelper('0px', SYS_SPACE_FIXED_150)}; 
    }
    .pac-item:not(:last-child) {
      border-block-end: ${dynamicStylingHelper('1px solid #d9d9d9', `2px solid hsla(240, 20%, 99%, 0.2)`)};
      padding-block-end: ${dynamicStylingHelper('10px', SYS_SPACE_FIXED_150)}; 
    }
    .pac-item:not(:first-child) {
      padding-block-start: ${dynamicStylingHelper('10px', SYS_SPACE_FIXED_150)};
    }
    .pac-item:hover, .pac-item-selected, .pac-item-selected:hover{
      background-color: ${dynamicStylingHelper('#f2f2f2', SYS_COLOR_SURFACE_NEUTRAL_700_DARK)};
      padding-inline-start: ${dynamicStylingHelper('8px', SYS_SPACE_FIXED_150)};
      cursor: pointer;
    }
    .pac-item:hover span, .pac-item-selected:hover span, .pac-item-selected span{
      color: ${dynamicStylingHelper('#000000', 'inherit')};
    }
    .marker-with-counter{
      margin-top: -6px;
    }
  `;
    return (React.createElement(LocationFilterHeaderContainer, null,
        isMobile && (React.createElement(StyledHeadline, { tag: "h4", variant: {
                order: '3',
                type: 'headline',
                weight: 'normal',
            } }, locationSearchHeadlineLabel)),
        !effectiveConsent && !isDesktop && (React.createElement(ConsentRequestWrapperMobile, null,
            React.createElement(CookieConsentRequest, { mapConsentButtonText: mapConsentButtonText, openConsentOverlay: openConsentOverlay, setIsTwoClickOverlayOpened: setIsTwoClickOverlayOpened }))),
        React.createElement(LocationInputContainer, null,
            React.createElement(SearchWrapper, null,
                React.createElement("style", null, autocompleteCSS),
                React.createElement(SearchField, { inputRef: inputRef, additionalIconButton: {
                        onClick: () => {
                            if (dynamicStylingHelper(true, false) && (inputRef === null || inputRef === void 0 ? void 0 : inputRef.current)) {
                                inputRef.current.value = '';
                            }
                            else {
                                handleLocateMeClick();
                            }
                        },
                        icon: dynamicStylingHelper('cancel', 'gps'),
                        'aria-label': dynamicStylingHelper(clearLocationSearchInputButtonAriaLabel, locateMeButtonAriaLabel),
                    }, disabled: !effectiveConsent, value: undefined, id: "locationSearchInput", label: locationSearchInputLabel, clearButton: {
                        'aria-label': clearLocationSearchInputButtonAriaLabel,
                        onClick: () => { },
                    } }),
                React.createElement(DynamicComponent, { legacy: React.createElement(LocateMeButton, { variant: "tertiary", icon: "gps", size: "sm", disabled: !effectiveConsent, onClick: () => handleLocateMeClick(), "aria-label": locateMeButtonAriaLabel }) })),
            React.createElement(StyledSelect, { id: "select_radius", label: distanceRadiusLabel, onChange: (e) => {
                    setRadius(e.target.value);
                }, value: radius, disabled: !effectiveConsent || !radius }, radiusOptions.map((radiusOption, index) => {
                return (React.createElement("option", { value: radiusOption, key: index },
                    "+ ",
                    radiusOption,
                    " ",
                    mileageUnit || 'km'));
            })),
            !enableMandatoryAreaSearch && (React.createElement(ClearAllLocationsButton, { variant: "tertiary", iconPosition: "leading", icon: dynamicStylingHelper('replay', 'erase'), size: "md", disabled: !locationIsSearchedOrDealerIsActive, onClick: () => {
                    setLocationFilterToDefaultState(getConfiguration(), getEnvironmentConfig(), clearAllDealerFilters, context, inputRef);
                    applyFilter(geoFilterDTO);
                } }, clearAllLocationsButtonText)))));
};
export default LocationFilterHeader;
const StyledHeadline = styled((props) => React.createElement(Headline, Object.assign({}, props))) `
  margin-bottom: ${SYS_SPACE_FIXED_450};
`;
